import Vue from "vue";
import _ from "lodash";
import {
    FetchState,
    InvestmentHistoryState,
    FetchStateKey,
    InvestmentHistory,
    InvestmentTransfer
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as bankService from "@/shared/api/bankService";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";

const emptyState: InvestmentHistoryState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    historyPages: {},
    historyPageCount: 0,
    historyPage: 0,
    historyTotalRecordCount: 0,
    historyIsPolling: false
};

export const useInvestmentHistoryStore = defineStore("investmentHistory", {
    state: (): InvestmentHistoryState => (_.cloneDeep(emptyState)),
    getters: {
        historyForCurrentPage (state): any {
            return (_.has(state.historyPages, state.historyPage) ?
                state.historyPages[state.historyPage] : []);
        },
    },
    actions: {
        setInvestmentStateLoading (key: FetchStateKey): void {
            this.fetchState = FetchState.Loading;
            this.fetchKey = key;
        },
        invalidateFetchState (key: FetchStateKey): void {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        setHistoryPage (data: InvestmentHistory): void {
            Vue.set(this.historyPages, `${data.page}`, data.records);
            this.historyPageCount = data.numberOfPages;
            this.historyPage = data.page;
        },
        setHistoryPageNumber (page: number): void {
            this.historyPage = page;
        },
        setHistoryTotalRecordCount (data: InvestmentHistory): void {
            this.historyTotalRecordCount = data.totalNumberOfRecords;
        },
        setInvestmentStateLoaded (): void {
            this.fetchState = FetchState.Loaded;
        },
        loadHistory (params: {
            pageNumber: number,
            username: string,
            partnerId: number,
            isPolling: boolean,
        }): Promise<any> {
            const fetchStateKey: FetchStateKey = {
                username: params.username,
                partnerId: params.partnerId
            };

            this.invalidateFetchState(fetchStateKey);

            // already loading ANYTHING then ditch out
            if (this.fetchState === FetchState.Loading) return Promise.resolve();

            if (!params.isPolling) {
                this.setInvestmentStateLoading(fetchStateKey);
            }

            return bankService.getInvestmentHistory(
                params.pageNumber,
                params.username,
                params.partnerId
            ).then(response => {
                    if (!fetchStateKeyMatch(this.fetchKey, fetchStateKey)) {
                        return;
                    }
                    this.setHistoryPage(response.data);
                    this.setHistoryTotalRecordCount(response.data);
                    this.setInvestmentStateLoaded();
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        pollHistory (): void {
            const previousHistoryTotalRecordCount = this.historyTotalRecordCount;
            let timesPolled = 0;

            const polling = setInterval(() => {
                if (this.historyTotalRecordCount > previousHistoryTotalRecordCount || timesPolled > 18) {
                    clearInterval(polling);
                }

                this.loadHistory({
                    pageNumber: 1,
                    isPolling: true
                });

                timesPolled++;
            }, 5000);

        }
    }
});